import { ReactNode, useCallback, useState } from 'react'
import { useDialog } from '../../hooks/useDialog'
import { useConfirm } from '../../hooks/useConfirm'
import { userService } from '../../services/userService'
import {
  AdminUser,
  CreateUserPayload,
  UpdateUserPayload,
  ResetPasswordPayload,
} from '../../types/auth'
import { Dialog } from '../../components/ui/Dialog'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { UserForm } from './UserForm'
import { ResetPasswordForm } from './ResetPasswordForm'

export type IntegrationProvider = 'google' | 'apple'

export interface UserActions {
  /** No argument = create a new user */
  openEdit: (item?: AdminUser) => void
  openResetPassword: (item: AdminUser) => void
  clearIntegration: (item: AdminUser, provider: IntegrationProvider) => void
  deactivate: (item: AdminUser) => void
  toggleSupportRestricted: (item: AdminUser) => void
}

/**
 * User admin actions shared by the users list and the user detail page: handlers plus the
 * dialogs they open. Render `dialogs` once in the page; `onChanged` runs after every change.
 */
export function useUserActions(
  svc: ReturnType<typeof userService>,
  onChanged: () => void
): { actions: UserActions; dialogs: ReactNode } {
  const editDialog = useDialog<AdminUser>()
  const resetPasswordDialog = useDialog<AdminUser>()
  const confirmDialog = useConfirm()

  const [saving, setSaving] = useState(false)
  const [userPayload, setUserPayload] = useState<CreateUserPayload | UpdateUserPayload | null>(null)
  const [passwordPayload, setPasswordPayload] = useState<ResetPasswordPayload | null>(null)

  const canSaveUser = editDialog.data
    ? !!(userPayload as UpdateUserPayload)?.email?.trim()
    : !!(userPayload as CreateUserPayload)?.email?.trim() &&
      (userPayload as CreateUserPayload)?.password?.length >= 8

  // ── Save user (create or update) ──────────────────────
  const handleSave = useCallback(async () => {
    if (!userPayload) return
    setSaving(true)
    try {
      if (editDialog.data) {
        await svc.update(editDialog.data.id, userPayload as UpdateUserPayload)
      } else {
        await svc.create(userPayload as CreateUserPayload)
      }
      editDialog.close()
      onChanged()
    } finally {
      setSaving(false)
    }
  }, [userPayload, editDialog, svc, onChanged])

  // ── Reset password ────────────────────────────────────
  const handleResetPassword = useCallback(async () => {
    if (!passwordPayload || !resetPasswordDialog.data) return
    setSaving(true)
    try {
      await svc.resetPassword(resetPasswordDialog.data.id, passwordPayload)
      resetPasswordDialog.close()
      onChanged()
    } finally {
      setSaving(false)
    }
  }, [passwordPayload, resetPasswordDialog, svc, onChanged])

  // ── Clear integration ─────────────────────────────────
  const clearIntegration = useCallback(
    (item: AdminUser, provider: IntegrationProvider, onAfter?: () => void) => {
      const providerLabel = provider.charAt(0).toUpperCase() + provider.slice(1)
      confirmDialog.confirm(
        `Disconnect ${providerLabel}`,
        `Remove the ${providerLabel} login connection for ${item.name ?? item.email}? They will need to use email and password to sign in.`,
        async () => {
          await svc.clearIntegration(item.id, provider)
          onAfter?.()
          onChanged()
        }
      )
    },
    [confirmDialog, svc, onChanged]
  )

  const handleClearInEdit = useCallback(
    (provider: IntegrationProvider) => {
      if (!editDialog.data) return
      clearIntegration(editDialog.data, provider, editDialog.close)
    },
    [editDialog, clearIntegration]
  )

  // ── Deactivate ────────────────────────────────────────
  const deactivate = useCallback(
    (item: AdminUser) => {
      confirmDialog.confirm(
        'Deactivate user',
        `Are you sure you want to deactivate ${item.name ?? item.email}? They will no longer be able to sign in.`,
        async () => {
          await svc.deactivate(item.id)
          onChanged()
        }
      )
    },
    [confirmDialog, svc, onChanged]
  )

  // ── Toggle support restriction ────────────────────────
  const toggleSupportRestricted = useCallback(
    (item: AdminUser) => {
      const action = item.supportRestricted ? 'unblock' : 'block'
      const label = item.name ?? item.email
      confirmDialog.confirm(
        `${item.supportRestricted ? 'Unblock' : 'Block'} support access`,
        `Are you sure you want to ${action} support access for ${label}?`,
        async () => {
          await svc.update(item.id, { supportRestricted: !item.supportRestricted })
          onChanged()
        }
      )
    },
    [confirmDialog, svc, onChanged]
  )

  const openEdit = useCallback((item?: AdminUser) => editDialog.open(item), [editDialog])

  const dialogs = (
    <>
      {/* ── Edit / Create dialog ───────────────── */}
      <Dialog
        open={editDialog.isOpen}
        title={editDialog.data ? 'Edit user' : 'Add user'}
        onClose={editDialog.close}
        onConfirm={handleSave}
        confirmLabel={editDialog.data ? 'Save changes' : 'Create user'}
        confirmLoading={saving}
        confirmDisabled={!canSaveUser}
      >
        <UserForm
          initial={editDialog.data}
          onChange={setUserPayload}
          onClearIntegration={editDialog.data ? handleClearInEdit : undefined}
        />
      </Dialog>

      {/* ── Reset password dialog ──────────────── */}
      <Dialog
        open={resetPasswordDialog.isOpen}
        title="Reset password"
        onClose={resetPasswordDialog.close}
        onConfirm={handleResetPassword}
        confirmLabel="Reset password"
        confirmLoading={saving}
      >
        {resetPasswordDialog.data && (
          <ResetPasswordForm user={resetPasswordDialog.data} onChange={setPasswordPayload} />
        )}
      </Dialog>

      {/* ── Confirm dialog ─────────────────────── */}
      <ConfirmDialog
        open={confirmDialog.state.open}
        title={confirmDialog.state.title}
        message={confirmDialog.state.message}
        confirmLabel="Confirm"
        onConfirm={confirmDialog.state.onConfirm}
        onCancel={confirmDialog.cancel}
        loading={confirmDialog.loading}
        variant="danger"
      />
    </>
  )

  return {
    actions: {
      openEdit,
      openResetPassword: resetPasswordDialog.open,
      clearIntegration,
      deactivate,
      toggleSupportRestricted,
    },
    dialogs,
  }
}
